import React from 'react';

import { ClockCircleOutlined, InboxOutlined, StopOutlined, SyncOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { TaskQueueStatusType } from 'types/tasks';

type Tone = 'blue' | 'green' | 'orange' | 'purple';

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const MetricCard = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 94px;
  padding: 18px;
  overflow: hidden;
  border: 1px solid var(--line-gray);
  border-radius: 10px;
  background: var(--white);
  box-shadow: 0 2px 8px var(--box-shadow-gray-dark);

  &::before {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    content: '';
  }

  &.blue::before {
    background: var(--active-blue);
  }

  &.green::before {
    background: var(--positive-green);
  }

  &.orange::before {
    background: var(--inactive-orange);
  }

  &.purple::before {
    background: var(--neutral-purple);
  }
`;

const MetricIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 9px;
  background: var(--background-gray-soft);
  font-size: 20px;

  &.blue {
    color: var(--active-blue);
  }

  &.green {
    color: var(--positive-green);
  }

  &.orange {
    color: var(--inactive-orange);
  }

  &.purple {
    color: var(--neutral-purple);
  }
`;

const MetricContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetricLabel = styled.span`
  color: var(--text-secondary);
  font-size: 12px;
  text-transform: uppercase;
`;

const MetricValue = styled.span`
  color: var(--text-primary);
  font-family: var(--font-bold);
  font-size: 28px;
`;

const Metric = ({ label, value, tone, icon }: { label: string; value?: number; tone: Tone; icon: React.ReactNode }) => (
  <MetricCard className={tone}>
    <MetricIcon className={tone}>{icon}</MetricIcon>
    <MetricContent>
      <MetricLabel>{label}</MetricLabel>
      <MetricValue>{value === undefined ? '—' : value.toLocaleString()}</MetricValue>
    </MetricContent>
  </MetricCard>
);

type Props = {
  status?: TaskQueueStatusType;
};

const TaskQueueMetrics = ({ status }: Props) => (
  <MetricsGrid>
    <Metric label="Pending" value={status?.pending_count} tone="blue" icon={<InboxOutlined />} />
    <Metric label="Reported executing" value={status?.executing_count} tone="green" icon={<SyncOutlined />} />
    <Metric label="Scheduled" value={status?.scheduled_count} tone="purple" icon={<ClockCircleOutlined />} />
    <Metric label="Revoked" value={status?.revoked_count} tone="orange" icon={<StopOutlined />} />
  </MetricsGrid>
);

export default TaskQueueMetrics;
